import { ArrowCcwIcon, TrashIcon } from "@xola/icons";
import { flash, Label, Modal, SubmitButton, Table } from "@xola/ui-kit";
import clsx from "clsx";
import { getPurchaseItemsByObject, handleSuccessfulSubmission } from "common";
import { pick, sortBy, truncate } from "lodash";
import React, { useEffect, useMemo, useState } from "react";
import { useMutation } from "react-query";
import { Except } from "type-fest";
import {
    LineItem,
    LineItemObject,
    LineItemType,
    PaymentMethod,
    Purchase,
    PurchaseItem,
    PurchaseItemObject,
    PurchaseStatus,
} from "types";
import { Currency } from "../Common/Currency";
import { SecondaryButton } from "../Common/SecondaryButton";
import { allowedKeysToMapPurchaseToStore } from "../PurchaseCreate/helpers/PurchaseCreateHelpers";
import { usePurchaseResponseStore } from "../PurchaseCreate/store/usePurchaseResponseStore";
import { lineItemsToIgnoreWhenApplyingCode } from "../PurchasesMenu/ApplySubmitCodeHelper";
import { PaymentBreakdown } from "./PaymentBreakdown";
import { PurchaseItemSelect } from "./PurchaseItemSelect";
import { api } from "@/api/api";
import { useUser } from "@/hooks/useAccount";
import {
    getCodes,
    getCouponCodes,
    getLineItemsByPaymentMethods,
    getMembershipCodes,
    isLineItemPurchaseLevel,
    isPurchaseLevelGift,
    isStoreCredit,
} from "@/models/LineItem";
import { isAffiliate } from "@/models/User";
import {
    CreatePurchaseItemLineItem,
    CreatePurchaseLineItem,
    CreatePurchaseState,
    useCreatePurchaseStore,
} from "@/stores/createPurchaseStore";
import { useLocalizationStore } from "@/stores/localizationStore";
import { getSeller } from "@/utils/getSeller";
import { updateCache } from "@/utils/updateCache";

export interface PurchasePayload {
    ops?: Ops[];
}

export interface Ops {
    path: string;
    op: string;
    data: {
        id: ID;
        object: LineItemObject;
    };
}

interface EditPurchaseTaxesAndFeesModalProps {
    isOpen: boolean;
    purchase: Purchase | CreatePurchaseState;
    item: PurchaseItem;
    onClose: () => void;
}

export const EditPurchaseCodesModal = ({ isOpen, purchase, item, onClose }: EditPurchaseTaxesAndFeesModalProps) => {
    return (
        <Modal isOpen={isOpen} size="huge" onClose={onClose}>
            <ModalContent purchase={purchase} item={item} onClose={onClose} />
        </Modal>
    );
};

interface LineItemToToggle {
    id: ID;
    object: LineItem["object"];
}

type EntranceType = LineItem | LineItemToToggle;

function getItemInList(lineItem: EntranceType, lineItemsList: EntranceType[] | undefined) {
    return lineItemsList?.find((item) => item.id === lineItem.id && item.object === lineItem.object);
}

const ModalContent = ({ purchase, item, onClose }: Except<EditPurchaseTaxesAndFeesModalProps, "isOpen">) => {
    const [selectedItem, setSelectedItem] = useState(item);
    const [lineItems, setLineItems] = useState(selectedItem.lineItems);
    const [purchaseLineItems, setPurchaseLineItems] = useState(purchase.lineItems as LineItem[]); // Store Credit
    const [lineItemsToToggle, setLineItemsToToggle] = useState<LineItemToToggle[]>([]);
    const eligibleItems = getPurchaseItemsByObject(purchase as Purchase, [
        PurchaseItemObject.EXPERIENCE,
        PurchaseItemObject.MEMBERSHIP,
    ]);

    const updateLineItemsToToggle = (lineItem: LineItem) => {
        const isExistLineItemToToggle = !!getItemInList(lineItem, lineItemsToToggle);
        if (isExistLineItemToToggle) {
            // Remove the given line item. Filter under two conditions -
            // The ids are not same, but object may be same e.g. 2 voucher codes applied on a reservation
            // The ids are same, but the object is different e.g. a gift with promotional discount & payment line items. Ref: X2-5729
            setLineItemsToToggle(
                lineItemsToToggle.filter((item) => item.id !== lineItem.id || item.object !== lineItem.object),
            );
        } else {
            // Add the given line item
            setLineItemsToToggle([...lineItemsToToggle, { id: lineItem.id, object: lineItem.object }]);
        }
    };

    const onToggleDelete = (toggledItem: LineItem) => {
        const isPaymentCoupon = toggledItem?.coupon && toggledItem?.type === LineItemType.PAYMENT;

        if (isStoreCredit(toggledItem) || isPurchaseLevelGift(toggledItem)) {
            const lineItems = purchaseLineItems?.map((lineItem: LineItem) => {
                if (toggledItem.id === lineItem.id) {
                    updateLineItemsToToggle(lineItem);
                }

                return lineItem;
            });
            setPurchaseLineItems(lineItems);
        }

        if (toggledItem.type === LineItemType.AFFILIATE_DEPOSIT || isPaymentCoupon) {
            const lineItems = purchaseLineItems.map((lineItem: LineItem) => {
                if (toggledItem.id === lineItem.id) {
                    updateLineItemsToToggle(lineItem);
                    return { ...lineItem, includeInAmount: !lineItem.includeInAmount };
                }

                return lineItem;
            });
            setPurchaseLineItems(lineItems);
        }

        setLineItems(
            lineItems.map((lineItem: LineItem) => {
                if (toggledItem.id === lineItem.id && toggledItem.object === lineItem.object) {
                    // when trying to delete affiliate discount, remove purchase line item. It will delete item line items as well
                    if (toggledItem.type === LineItemType.AFFILIATE_DISCOUNT) {
                        const purchaseLineItemsFiltered = getLineItemsByPaymentMethods(
                            [PaymentMethod.AFFILIATE_DEPOSIT],
                            purchaseLineItems,
                        );
                        const affiliateDepositLineItem = purchaseLineItemsFiltered[0];
                        if (affiliateDepositLineItem) {
                            updateLineItemsToToggle(affiliateDepositLineItem);
                        }
                    } else {
                        updateLineItemsToToggle(lineItem);
                    }

                    return { ...lineItem, includeInAmount: !lineItem.includeInAmount };
                }

                return lineItem;
            }),
        );
    };

    const {
        mutateAsync: modifyCodes,
        isLoading,
        isSuccess,
    } = useMutation(async (payload: PurchasePayload) => {
        try {
            const { data: updatedPurchase } = await api.patch<Purchase>(`/purchases/${purchase.id}`, payload);

            if (purchase.status === PurchaseStatus.DRAFT) {
                usePurchaseResponseStore.getState().actions.setPurchase(updatedPurchase);
                useCreatePurchaseStore.getState().actions.setPurchase((draft) => {
                    for (const [index, item] of updatedPurchase.items.entries()) {
                        const lineItems = item.lineItems
                            .filter((lineItem) => !lineItemsToIgnoreWhenApplyingCode.includes(lineItem.type))
                            .map((lineItem) => pick(lineItem, allowedKeysToMapPurchaseToStore));

                        draft.items[index].lineItems = lineItems as CreatePurchaseItemLineItem[];
                    }

                    draft.lineItems = updatedPurchase.lineItems.map((lineItem: LineItem) => {
                        return pick(lineItem, allowedKeysToMapPurchaseToStore) as CreatePurchaseLineItem;
                    });
                });
                useCreatePurchaseStore.setState({
                    balance: updatedPurchase.balance,
                    dueNow: updatedPurchase.dueNow,
                    amount: updatedPurchase.amount,
                });
            }

            updateCache("purchases", updatedPurchase);
        } catch {
            flash.danger("Error saving Codes");
        }
    });

    const updatedPurchase = useMemo(() => {
        // First should be removed coupon_line_item and after that payment_line_item
        const ops = sortBy(lineItemsToToggle, "object").map((toggleItem) => {
            const lineItem = getItemInList(toggleItem, lineItems) ?? getItemInList(toggleItem, purchaseLineItems);

            const isPurchaseLevel = isLineItemPurchaseLevel(lineItem as LineItem);

            return {
                path: isPurchaseLevel ? `/lineItems` : `/items/${selectedItem.id}/lineItems`,
                op: "removeLineItem",
                data: {
                    id: toggleItem.id,
                    object: isPurchaseLevel ? LineItemObject.PAYMENT : LineItemObject.COUPON,
                },
            };
        });

        return ops ? { ops } : {};
    }, [lineItemsToToggle, selectedItem.id, lineItems, purchaseLineItems]);

    const handleSubmit = async () => {
        if ("ops" in updatedPurchase) {
            await modifyCodes(updatedPurchase);
        }

        handleSuccessfulSubmission(onClose);
    };

    return (
        <>
            <Modal.Header>Modify Codes</Modal.Header>

            <Modal.Body className="flex-col space-y-4">
                <PurchaseItemSelect
                    items={eligibleItems}
                    value={selectedItem}
                    className="flex-1"
                    onChange={(item: PurchaseItem) => {
                        setSelectedItem(item);
                        setLineItems(item.lineItems);
                    }}
                />
                <div className="flex space-x-4">
                    <div className="w-1/2 flex-1 space-y-4">
                        <CodesList
                            title="Store Credit"
                            lineItems={getLineItemsByPaymentMethods([PaymentMethod.STORE_CREDIT], purchaseLineItems)}
                            lineItemsToToggle={lineItemsToToggle}
                            onToggleDelete={onToggleDelete}
                        />
                        <CodesList
                            title="Gift"
                            lineItems={getLineItemsByPaymentMethods([PaymentMethod.GIFT], purchaseLineItems)}
                            lineItemsToToggle={lineItemsToToggle}
                            onToggleDelete={onToggleDelete}
                        />
                        <CodesList
                            title="Coupon Codes"
                            lineItems={getCouponCodes(lineItems, purchaseLineItems)}
                            onToggleDelete={onToggleDelete}
                        />
                        <CodesList
                            title="Membership Codes"
                            lineItems={getMembershipCodes(lineItems)}
                            onToggleDelete={onToggleDelete}
                        />
                        <CodesList
                            title="Agent Codes"
                            item={item}
                            isDepositAndDiscountAffiliateExist={checkIsDepositAndDiscountAffiliateExist(
                                lineItems,
                                purchaseLineItems,
                            )}
                            lineItems={[
                                ...getCodes(
                                    [
                                        LineItemType.AFFILIATE_COMMISSION,
                                        LineItemType.AFFILIATE_DEPOSIT,
                                        LineItemType.AFFILIATE_DISCOUNT,
                                    ],
                                    lineItems,
                                ),
                                ...getLineItemsByPaymentMethods([PaymentMethod.AFFILIATE_DEPOSIT], purchaseLineItems),
                            ]}
                            onToggleDelete={onToggleDelete}
                        />
                    </div>
                    <div className="flex-1">
                        <div className="bg-gray-lighter p-6">
                            <PaymentBreakdown
                                isEditable={false}
                                purchase={{ ...purchase } as Purchase}
                                item={selectedItem}
                                payload={updatedPurchase ?? {}}
                            />
                        </div>
                    </div>
                </div>
            </Modal.Body>
            <Modal.Footer>
                <fieldset className="flex justify-end space-x-4">
                    <SecondaryButton onClick={onClose}>
                        <div>Cancel</div>
                    </SecondaryButton>

                    <SubmitButton isLoading={isLoading} isSuccess={isSuccess} onClick={handleSubmit}>
                        Confirm
                    </SubmitButton>
                </fieldset>
            </Modal.Footer>
        </>
    );
};

interface CodeItemProps {
    lineItem: LineItem;
    lineItemForAffiliateDeleting: LineItem | null;
    onToggleDelete: (lineItem: LineItem) => void;
    lineItemsToToggle?: LineItemToToggle[];
    isAffiliateDeletedForDepositAndDiscount?: boolean;
    isDepositAndDiscountAffiliateExist?: boolean;
    item?: PurchaseItem;
}

const AffiliateLineItems = new Set([
    LineItemType.AFFILIATE_DISCOUNT,
    LineItemType.AFFILIATE_DEPOSIT,
    LineItemType.AFFILIATE_COMMISSION,
]);

const CodeItem = ({
    lineItem,
    lineItemForAffiliateDeleting,
    onToggleDelete,
    lineItemsToToggle,
    isAffiliateDeletedForDepositAndDiscount,
    isDepositAndDiscountAffiliateExist,
    item,
}: CodeItemProps) => {
    const [isSetForRemoval, setIsSetForRemoval] = useState(false);

    const user = useUser();
    const locale = useLocalizationStore().getLocale();

    const isDeleteDisabled = lineItem.type === LineItemType.AFFILIATE_COMMISSION;
    const isPartnerSellerBooking = !!(item && item.seller?.id !== getSeller().id);

    const handleToggleDelete = () => {
        if (lineItemForAffiliateDeleting) {
            onToggleDelete(lineItemForAffiliateDeleting);
        }

        if (!lineItemForAffiliateDeleting) {
            onToggleDelete(lineItem);
        }

        setIsSetForRemoval((previous) => !previous);
    };

    // This useEffect added because line items in purchase.item[n] with AFFILIATE_DEPOSIT type always have includeInAmount = false
    // So this useEffect allows to avoid incorrect applied styles for AFFILIATE_DEPOSIT items
    useEffect(() => {
        const isPaymentCoupon = lineItem?.coupon && lineItem?.type === LineItemType.PAYMENT;

        if (isStoreCredit(lineItem) || isPurchaseLevelGift(lineItem)) {
            const isExistLineItemToToggle = !!getItemInList(lineItem, lineItemsToToggle);
            if (isExistLineItemToToggle) {
                setIsSetForRemoval(isExistLineItemToToggle);
                return;
            }

            return;
        }

        if ((lineItem.type === LineItemType.AFFILIATE_DEPOSIT || isPaymentCoupon) && !lineItem.includeInAmount) {
            setIsSetForRemoval(lineItem.includeInAmount);
            return;
        }

        if (!lineItem.includeInAmount) {
            setIsSetForRemoval(!lineItem.includeInAmount);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return isDeleteDisabled ? null : (
        <Table.Row
            className={clsx(
                (isDepositAndDiscountAffiliateExist && isAffiliateDeletedForDepositAndDiscount) || isSetForRemoval
                    ? "line-through"
                    : "",
            )}
            data-testid="code-item"
        >
            <Table.Cell className="no-translate">{truncate(lineItem.name, { length: 20 })}</Table.Cell>
            <Table.Cell>
                <Currency shouldRemoveTrailingZeroes={false} locale={locale}>
                    {Math.abs(lineItem.amount)}
                </Currency>
            </Table.Cell>
            <Table.Cell>
                <SecondaryButton
                    size="small"
                    data-testid="code-delete-button"
                    disabled={(isAffiliate(user) && AffiliateLineItems.has(lineItem.type)) || isPartnerSellerBooking}
                    onClick={handleToggleDelete}
                >
                    {(isDepositAndDiscountAffiliateExist && isAffiliateDeletedForDepositAndDiscount) ||
                    isSetForRemoval ? (
                        <ArrowCcwIcon />
                    ) : (
                        <TrashIcon />
                    )}
                </SecondaryButton>
            </Table.Cell>
        </Table.Row>
    );
};

const CodeHeader = () => {
    return (
        <Table.Head>
            <Table.Row>
                <Table.Header>Name</Table.Header>
                <Table.Header>Amount</Table.Header>
                <Table.Header />
            </Table.Row>
        </Table.Head>
    );
};

interface CodesListProps {
    title: string;
    lineItems: LineItem[];
    isDepositAndDiscountAffiliateExist?: boolean;
    onToggleDelete: (lineItem: LineItem) => void;
    lineItemsToToggle?: LineItemToToggle[];
    item?: PurchaseItem;
}

const CodesList = ({
    title,
    lineItems,
    onToggleDelete,
    lineItemsToToggle,
    isDepositAndDiscountAffiliateExist,
    item,
}: CodesListProps) => {
    const [isAffiliateDeletedForDepositAndDiscount, setIsAffiliateDeletedForDepositAndDiscount] = useState(false);
    const [lineItemForAffiliateDeleting, setLineItemForAffiliateDeleting] = useState<LineItem | null>(null);

    const handleOnToggleDelete = (lineItem: LineItem) => {
        onToggleDelete(lineItem);

        if (isDepositAndDiscountAffiliateExist) {
            if (lineItemForAffiliateDeleting) {
                setLineItemForAffiliateDeleting(null);
            } else {
                setLineItemForAffiliateDeleting(lineItem);
            }

            setLineItemForAffiliateDeleting(lineItem);
            setIsAffiliateDeletedForDepositAndDiscount((previous) => !previous);
        }
    };

    return lineItems.length > 0 ? (
        <>
            <Label data-testid="edit-purchase-codes-modal-codes-list-label">{title}</Label>
            <Table>
                <CodeHeader />
                <Table.Body>
                    {lineItems.map((lineItem: LineItem) => (
                        <CodeItem
                            key={lineItem.id}
                            lineItem={lineItem}
                            lineItemsToToggle={lineItemsToToggle}
                            isAffiliateDeletedForDepositAndDiscount={isAffiliateDeletedForDepositAndDiscount}
                            isDepositAndDiscountAffiliateExist={isDepositAndDiscountAffiliateExist}
                            lineItemForAffiliateDeleting={lineItemForAffiliateDeleting}
                            item={item}
                            onToggleDelete={handleOnToggleDelete}
                        />
                    ))}
                </Table.Body>
            </Table>
        </>
    ) : null;
};

const checkIsDepositAndDiscountAffiliateExist = (lineItems: LineItem[], purchaseLineItems: LineItem[]) => {
    const itemLineItemsFiltered = getCodes(
        [LineItemType.AFFILIATE_COMMISSION, LineItemType.AFFILIATE_DEPOSIT, LineItemType.AFFILIATE_DISCOUNT],
        lineItems,
    );

    const purchaseLineItemsFiltered = getLineItemsByPaymentMethods(
        [PaymentMethod.AFFILIATE_DEPOSIT],
        purchaseLineItems,
    );

    return Boolean(itemLineItemsFiltered.length > 0 && purchaseLineItemsFiltered.length > 0);
};
